import React from 'react'
import { useHistory } from 'react-router-dom'
import { downloadImage } from 'src/api/services/amplify'
import {
  ILearningModule,
  ILearningPage,
  ILearningSection
} from 'src/models/learning'
import { PageLevelHeading } from 'src/shared/styled/Text'
import styled from 'styled-components'

const BackButton = styled.button`
  background: none;
  border: none;
  color: #1565c0;
  cursor: pointer;
  padding: 0;
  margin-bottom: 16px;
`

const Meta = styled.p`
  margin: 4px 0;
`

const PageBlock = styled.section`
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
`

const SectionBlock = styled.div`
  margin: 12px 0 12px 16px;
`

const SectionImage = ({ imageKey }: { imageKey: string }) => {
  const [src, setSrc] = React.useState<string | null>(null)

  React.useEffect(() => {
    let active = true
    downloadImage(imageKey).then((url) => {
      if (active) setSrc(url)
    })
    return () => {
      active = false
    }
  }, [imageKey])

  if (!src) return null
  return <img src={src} alt="" style={{ maxWidth: '320px' }} />
}

const Section = ({ section }: { section: ILearningSection }) => {
  if (section.type === 'TextSection') {
    return <SectionBlock>{section.text}</SectionBlock>
  }

  if (section.type === 'ImageSection') {
    return (
      <SectionBlock>
        {section.imageUrl ? <SectionImage imageKey={section.imageUrl} /> : null}
        {section.description ? <p>{section.description}</p> : null}
      </SectionBlock>
    )
  }

  if (section.type === 'QuestionSection' && section.question) {
    return (
      <SectionBlock>
        <p>{section.question.questionText}</p>
        {section.question.questionElaboration ? (
          <p>{section.question.questionElaboration}</p>
        ) : null}
        <ul>
          {(section.question.answerOptions || []).map((option) => (
            <li key={option.indexLabel || option.text}>
              {option.text}
              {option.correct ? ' (correct)' : ''}
            </li>
          ))}
        </ul>
      </SectionBlock>
    )
  }

  return null
}

const Page = ({ page, index }: { page: ILearningPage; index: number }) => (
  <PageBlock>
    <h3>{page.header || `Page ${index + 1}`}</h3>
    {page.description ? <p>{page.description}</p> : null}
    {page.imageUrl ? <SectionImage imageKey={page.imageUrl} /> : null}
    {(page.sections || []).map((section) => (
      <Section key={section.id} section={section} />
    ))}
  </PageBlock>
)

const LearningModuleDetail = ({
  learningModule
}: {
  learningModule: ILearningModule
}) => {
  const history = useHistory()

  return (
    <div>
      <BackButton type="button" onClick={() => history.push('/learning')}>
        Back to Learning Modules
      </BackButton>
      <PageLevelHeading>{learningModule.title}</PageLevelHeading>
      <Meta>Status: {learningModule.status}</Meta>
      {learningModule.estimatedDuration ? (
        <Meta>Time to complete: {learningModule.estimatedDuration}</Meta>
      ) : null}
      {learningModule.description ? <p>{learningModule.description}</p> : null}
      {learningModule.coverImageUrl ? (
        <SectionImage imageKey={learningModule.coverImageUrl} />
      ) : null}
      {(learningModule.pages || []).map((page, index) => (
        <Page key={page.pageId} page={page} index={index} />
      ))}
    </div>
  )
}

export default LearningModuleDetail
